import React, { useState } from 'react';
import { Search, User, ShoppingCart, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const NAV_LINKS = [
  { to: '/', label: 'Accueil' },
  { to: '/produits', label: 'Produits' },
  { to: '/devis', label: 'Devis sur mesure', testId: 'nav-devis' },
  { to: '/contact', label: 'Contact' },
];

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const { getCartCount } = useCart();
  const { user } = useAuth();
  const cartCount = getCartCount();

  const accountHref = user ? (user.role === 'admin' ? '/admin' : '/mon-compte') : '/connexion';

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3 md:py-4">
        <div className="flex items-center justify-between gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" onClick={() => setMobileOpen(false)}>
            <img
              src="https://customer-assets.emergentagent.com/job_fbe0f837-d33d-4f84-bae0-21bace0d1680/artifacts/n9bb0eio_NEOPLIAGE%20sans%20fond.png"
              alt="NEOPLIAGE"
              className="h-14 sm:h-16 md:h-[88px] lg:h-[102px]"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className="text-gray-700 hover:text-[#E42313] font-medium transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-3 md:gap-5">
            {/* Search desktop */}
            <div className="relative hidden xl:block">
              <input
                type="text"
                placeholder="Rechercher"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-56 px-4 py-2 pr-10 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>

            {/* Account */}
            <Link
              to={accountHref}
              className="flex flex-col items-center text-gray-600 hover:text-[#E42313] transition-colors"
              data-testid="header-account-link"
            >
              <User className="w-6 h-6" />
              <span className="text-[10px] sm:text-xs mt-0.5 hidden sm:block">
                {user ? (user.name?.split(' ')[0] || 'Compte') : 'Compte'}
              </span>
            </Link>

            {/* Cart */}
            <Link
              to="/panier"
              className="flex flex-col items-center text-gray-600 hover:text-[#E42313] transition-colors relative"
              data-testid="header-cart-link"
            >
              <ShoppingCart className="w-6 h-6" />
              <span className="text-[10px] sm:text-xs mt-0.5 hidden sm:block">Panier</span>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#E42313] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 -mr-2 text-gray-700 hover:text-[#E42313]"
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu-drawer">
          <nav className="container mx-auto px-4 py-3 flex flex-col">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMobileOpen(false)}
                className="py-3 border-b border-gray-100 text-gray-800 hover:text-[#E42313] font-medium"
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-3 relative">
              <input
                type="text"
                placeholder="Rechercher"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
